import { openDB, IDBPDatabase } from 'idb';
import { GameState } from '../types/game';

// Database configuration
const DB_NAME = 'cryptoClickerDB';
const DB_VERSION = 3; // Increment version to trigger upgrade

// Database schema
interface GameDB {
  gameState: {
    key: string;
    value: GameState;
  };
  purchases: {
    key: string;
    value: {
      multipliers: { [key: string]: number };
      autoClickers: Array<{
        type: number;
        owned: number;
        maxOwned: number;
      }>;
      bonuses: { [key: string]: number };
    };
  };
  userProgress: {
    key: string;
    value: {
      balance: number;
      pending_wei: string;
      [key: string]: any;
    };
  };
  achievements: {
    key: string;
    value: {
      progress: Record<string, any>;
      unlocked: Array<any>;
    };
  };
}

// Initialize IndexedDB
async function initDB(): Promise<IDBPDatabase<GameDB>> {
  return openDB<GameDB>(DB_NAME, DB_VERSION, {
    upgrade(db: IDBPDatabase<GameDB>, oldVersion: number, newVersion: number | null) {
      // Create stores if they don't exist
      if (!db.objectStoreNames.contains('gameState')) {
        db.createObjectStore('gameState');
      }
      if (!db.objectStoreNames.contains('purchases')) {
        db.createObjectStore('purchases');
      }
      if (!db.objectStoreNames.contains('userProgress')) {
        db.createObjectStore('userProgress');
      }
      if (!db.objectStoreNames.contains('achievements')) {
        db.createObjectStore('achievements');
      }
    },
    blocked() {
      console.warn('Database upgrade blocked. Please close other tabs using the application.');
    },
    blocking() {
      console.warn('Database blocking upgrade in another tab.');
    },
    terminated() {
      console.error('Database connection terminated unexpectedly.');
    }
  });
}

// Database interface
export const gameDB = {
  async saveGameState(walletAddress: string, state: GameState): Promise<void> {
    const db = await initDB();
    await db.put('gameState', state, walletAddress);
  },

  async loadGameState(walletAddress: string): Promise<GameState | null> {
    const db = await initDB();
    return await db.get('gameState', walletAddress);
  },

  async savePurchases(walletAddress: string, purchases: any): Promise<void> {
    const db = await initDB();
    await db.put('purchases', purchases, walletAddress);
  },

  async loadPurchases(walletAddress: string): Promise<any> {
    const db = await initDB();
    try {
      const purchases = await db.get('purchases', walletAddress);
      if (!purchases) {
        // Initialize with default values if no purchases exist
        const defaultPurchases = {
          multipliers: {},
          autoClickers: [],
          bonuses: {}
        };
        await db.put('purchases', defaultPurchases, walletAddress);
        return defaultPurchases;
      }
      return purchases;
    } catch (error) {
      console.error('Error loading purchases:', error);
      throw error;
    }
  },

  async saveAchievements(walletAddress: string, achievements: any): Promise<void> {
    const db = await initDB();
    await db.put('achievements', achievements, walletAddress);
  },

  async loadAchievements(walletAddress: string): Promise<any> {
    const db = await initDB();
    try {
      const achievements = await db.get('achievements', walletAddress);
      if (!achievements) {
        // Initialize with default values if no achievements exist
        const defaultAchievements = {
          progress: {},
          unlocked: []
        };
        await db.put('achievements', defaultAchievements, walletAddress);
        return defaultAchievements;
      }
      return achievements;
    } catch (error) {
      console.error('Error loading achievements:', error);
      throw error;
    }
  },

  async getUserProgress(walletAddress: string): Promise<any> {
    const db = await initDB();
    try {
      const progress = await db.get('userProgress', walletAddress);
      if (!progress) {
        // Initialize with default values if no progress exists
        const defaultProgress = {
          balance: 0,
          pending_wei: '0'
        };
        await db.put('userProgress', defaultProgress, walletAddress);
        return defaultProgress;
      }
      return progress;
    } catch (error) {
      console.error('Error getting user progress:', error);
      throw error;
    }
  },

  async saveUserProgress(walletAddress: string, progress: any): Promise<void> {
    const db = await initDB();
    try {
      const currentProgress = await this.getUserProgress(walletAddress);
      await db.put('userProgress', {
        ...currentProgress,
        ...progress,
        last_updated: new Date().toISOString()
      }, walletAddress);
    } catch (error) {
      console.error('Error saving user progress:', error);
      throw error;
    }
  },

  async clearData(walletAddress: string): Promise<void> {
    const db = await initDB();
    try {
      await Promise.all([
        db.delete('gameState', walletAddress),
        db.delete('purchases', walletAddress),
        db.delete('userProgress', walletAddress),
        db.delete('achievements', walletAddress)
      ]);
    } catch (error) {
      console.error('Error clearing data:', error);
      throw error;
    }
  }
};